/* eslint-disable max-lines-per-function */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Dropdown, DropdownButton, Spinner } from 'react-bootstrap';

import {
  CreateUpdateSalesInvoiceDto,
  CreateUpdateSalesInvoiceDtoStatusEnum,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { isMainCurrencyPLN } from '../../../utils/currencyFormatUtils';
import { useTranslation } from '../../../common/hooks/useTranslation';

interface SalesInvoicePrintControlsProps {
  salesInvoice: CreateUpdateSalesInvoiceDto;
  invoiceStatus: CreateUpdateSalesInvoiceDtoStatusEnum;
  id?: string;
  setIsButtonsBlocked: Dispatch<SetStateAction<boolean>>;
}

const getDropdownTitle = (isLoading: boolean, title: string) => (
  <>
    {isLoading && <Spinner size={'sm'} className="me-1" />}
    {title}
  </>
);

export const SalesInvoicePrintControls: React.FC<
  SalesInvoicePrintControlsProps
> = props => {
  const { setIsButtonsBlocked } = props;
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isPrintoutLoading, setIsPrintoutLoading] = useState(false);
  const [hasInvoiceSavedPdf, setHasInvoiceSavedPdf] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const notifications = useNotifications();
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvsLng');

  const shouldDisableButtons =
    isPrintLoading || isDuplicateLoading || isCopyLoading || isPrintoutLoading;

  const printInvoice = async (
    download: boolean,
    duplicate: boolean,
    copy: boolean,
  ) => {
    try {
      if (download) {
        await pok.pdfPrinters.downloadInvoice(props.id!, duplicate, copy);
      } else {
        await pok.pdfPrinters.openInvoice(props.id!, duplicate, copy);
      }
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    } finally {
      setIsPrintLoading(false);
      setIsDuplicateLoading(false);
      setIsCopyLoading(false);
    }
    setRefresh(!refresh);
  };

  const invoiceDataPrintout = async () => {
    try {
      setIsPrintoutLoading(true);
      await pok.pdfPrinters.invoiceDataPrintout(props.id!);
      setIsPrintoutLoading(false);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
      setIsPrintoutLoading(false);
    }
  };

  useEffect(() => {
    if (
      props.id &&
      props.invoiceStatus !== CreateUpdateSalesInvoiceDtoStatusEnum.Draft
    ) {
      pok.savedPdfs
        .hasInvoiceSavedPdf(props.id)
        .then(setHasInvoiceSavedPdf)
        .catch(async errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }
  }, [notifications, pok.savedPdfs, props.id, props.invoiceStatus, refresh]);

  useEffect(() => {
    setIsButtonsBlocked(shouldDisableButtons);
  }, [setIsButtonsBlocked, shouldDisableButtons]);

  return (
    <>
      {((props.invoiceStatus !== CreateUpdateSalesInvoiceDtoStatusEnum.Draft &&
        !props.salesInvoice.toKsef) ||
        (props.invoiceStatus === CreateUpdateSalesInvoiceDtoStatusEnum.InKseF &&
          props.salesInvoice.toKsef)) && (
        <>
          <DropdownButton
            title={getDropdownTitle(isPrintLoading, t(tk.finance.print))}
            id="print-dropdown"
            variant="primary"
            className="d-inline-block"
            disabled={shouldDisableButtons}
          >
            <Dropdown.Item
              onClick={() => {
                setIsPrintLoading(true);
                return printInvoice(true, false, false);
              }}
              eventKey="1"
            >
              {t(tk.button.download)}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                setIsPrintLoading(true);
                return printInvoice(false, false, false);
              }}
              eventKey="2"
            >
              {t(tk.button.open)}
            </Dropdown.Item>
          </DropdownButton>
          {isMainCurrencyPLN() && (
            <Button
              variant="primary"
              onClick={() => invoiceDataPrintout()}
              isLoading={isPrintoutLoading}
              disabled={shouldDisableButtons}
            >
              {t(tk.finance.fvsData)}
            </Button>
          )}
        </>
      )}
      {((props.invoiceStatus !== CreateUpdateSalesInvoiceDtoStatusEnum.Draft &&
        !props.salesInvoice.toKsef) ||
        (props.invoiceStatus === CreateUpdateSalesInvoiceDtoStatusEnum.InKseF &&
          props.salesInvoice.toKsef)) &&
        hasInvoiceSavedPdf && (
          <>
            <DropdownButton
              title={getDropdownTitle(
                isDuplicateLoading,
                t(tk.finance.duplicate),
              )}
              id="print-dropdown"
              variant="primary"
              className="d-inline-block"
              disabled={shouldDisableButtons}
            >
              <Dropdown.Item
                onClick={() => {
                  setIsDuplicateLoading(true);
                  return printInvoice(true, true, false);
                }}
                eventKey="1"
              >
                {t(tk.button.download)}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setIsDuplicateLoading(true);
                  return printInvoice(false, true, false);
                }}
                eventKey="2"
              >
                {t(tk.button.open)}
              </Dropdown.Item>
            </DropdownButton>
            <DropdownButton
              title={getDropdownTitle(isCopyLoading, t(tk.finance.copy))}
              id="print-dropdown"
              variant="primary"
              className="d-inline-block"
              disabled={shouldDisableButtons}
            >
              <Dropdown.Item
                onClick={() => {
                  setIsCopyLoading(true);
                  return printInvoice(true, false, true);
                }}
                eventKey="1"
              >
                {t(tk.button.download)}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setIsCopyLoading(true);
                  return printInvoice(false, false, true);
                }}
                eventKey="2"
              >
                {t(tk.button.open)}
              </Dropdown.Item>
            </DropdownButton>
          </>
        )}
    </>
  );
};
